import React from 'react';

import { Typography, Box } from '@mui/material';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import ProductBadge, { PlusButton } from './ProductBadge';
import { getManyTagsBySlug } from '@/utils/tags/getTagBySlug';

const DISPLAY_LIMIT = 5;

const Wrapper = styled(Box)`
  display: flex;
  justify-content: flex-start;

  @media screen and (max-width: 700px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.31rem;
`;

interface RelevantForProps {
  articleProducts?: string[];
  allProducts?: Product[];
}

export default function RelevantFor({ articleProducts, allProducts }: RelevantForProps) {
  const translate = useTranslations();

  const [showAll, setShowAll] = React.useState(false);

  const mappedProducts = React.useMemo(() => {
    if (!articleProducts || !allProducts) {
      return [];
    }

    const tags = getManyTagsBySlug({
      tags: allProducts,
      slugs: articleProducts,
    });

    return tags;
  }, [articleProducts, allProducts]);

  const tagsToDisplay = React.useMemo(() => {
    if (mappedProducts.length <= DISPLAY_LIMIT || showAll) {
      return mappedProducts;
    }

    return mappedProducts.slice(0, DISPLAY_LIMIT);
  }, [mappedProducts, showAll]);

  if (!mappedProducts || mappedProducts.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      {/* no wrap */}
      <Typography variant="body1" fontWeight="bold" marginRight={2} sx={{ whiteSpace: 'nowrap' }}>
        {translate('relevantFor')}
      </Typography>
      <Badges>
        {tagsToDisplay.map(product => (
          <ProductBadge key={product.id} product={product} noLink />
        ))}
        {mappedProducts.length > DISPLAY_LIMIT && showAll === false && (
          <PlusButton onClickAction={() => setShowAll(true)} />
        )}
      </Badges>
    </Wrapper>
  );
}
